/**
 * Keeps the two ends of a shared game feeling live without anyone tapping
 * Refresh — at the smallest Cloudflare cost we can manage (see `livePoll.ts`
 * for the polling rules, `relay/gameShare.ts` for what each call costs).
 *
 * HOST (`watchGuestRounds`): while the play screen of a game that has been
 * shared is showing, one cheap call every ~20 s asks "has a guest submitted
 * something?". Only when the answer changes does it ask how many are waiting,
 * and shows that on the "Check for Guest Rounds" button. It NEVER applies a
 * round: a guest's round still counts only after the host taps the button and
 * it goes through the normal validation.
 *
 * GUEST (`watchSharedGame`): every ~30 s, "has the host published anything
 * since the version I have?". An unchanged game costs a few bytes back. A
 * change updates the scoreboard and round list IN PLACE — never re-rendering
 * the form, so a hand being entered is not wiped.
 *
 * Neither runs for a game that was never shared, while the screen is hidden,
 * or after the person has left the screen.
 */

import {
  fetchPending,
  fetchPendingCount,
  fetchShareSignal,
  fetchShareStateIfChanged,
  getStoredShare,
  type SharedGameState,
} from './gameShare';
import { startPoller, type Poller, type TickResult } from './livePoll';

export const HOST_POLL_MS = 20_000;

/**
 * After the host has checked (an exact answer), the cheap count — a KV LIST on the relay — can still list a round that was just
 * consumed: lists are eventually consistent and can lag by about a minute. For that long a recount asks for the rounds themselves
 * instead, which is exact. Beyond it the cheap count is trusted again.
 */
export const EXACT_RECOUNT_MS = 90_000;

/** True while a recount must not trust the relay's cheap (list-based) count. Pure, so it is tested without a clock. */
export function needsExactRecount(now: number, lastCheckAt: number): boolean {
  return lastCheckAt > 0 && now - lastCheckAt < EXACT_RECOUNT_MS;
}
export const GUEST_POLL_MS = 30_000;

// ---------------------------------------------------------------------------
// Host: "guest rounds waiting" badge
// ---------------------------------------------------------------------------

const CHECK_BUTTON_ID = 'checkSubmissionsBtn';

/** The button's text — the count only when something is waiting. */
export function checkGuestRoundsLabel(waiting: number): string {
  return waiting > 0 ? `Check for Guest Rounds (${waiting})` : 'Check for Guest Rounds';
}

const CHECKED_NOTE_ID = 'guestCheckedNote';

let hostPoller: Poller | undefined;
let hostGameId: string | undefined;
let waiting = 0;
/** Bumped each time the host's own check settles the count, so a recount that was already in flight cannot paint an older answer over it. */
let countEpoch = 0;
let lastCheckAt = 0;
let lastSignal: string | null | undefined;
/** When the relay last answered a check for guest rounds, and whether the most recent try failed. */
let lastCheckedAt: Date | undefined;
let checkFailing = false;

const clock = (d: Date): string => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * The line under the Check button, so the host can see at a glance whether the screen is
 * current: when the relay last answered, or that it can't be reached right now. Empty for a
 * game that was never shared.
 */
export function guestRoundsNote(): string {
  if (!hostPoller?.isRunning() && !lastCheckedAt && !checkFailing) return '';
  if (checkFailing) {
    return lastCheckedAt
      ? `Can't reach the server — guest rounds last checked ${clock(lastCheckedAt)}. Trying again automatically.`
      : "Can't reach the server yet. Trying again automatically.";
  }
  return lastCheckedAt ? `Guest rounds last checked ${clock(lastCheckedAt)}` : 'Checking for guest rounds…';
}

function paintNote(): void {
  const note = document.getElementById(CHECKED_NOTE_ID);
  if (!note) return;
  const text = guestRoundsNote();
  note.textContent = text;
  note.hidden = text === '';
  note.classList.toggle('live-note-problem', checkFailing);
}

/** A manual "Check for Guest Rounds" got an answer from the relay. */
export function noteGuestRoundsChecked(): void {
  lastCheckedAt = new Date();
  checkFailing = false;
  paintNote();
}

export function guestRoundsWaiting(): number {
  return waiting;
}

function paintBadge(): void {
  const button = document.getElementById(CHECK_BUTTON_ID);
  if (!button) return;
  button.textContent = checkGuestRoundsLabel(waiting);
  // Waiting rounds are the one thing on this screen that needs the host, so the button steps up from "secondary".
  button.classList.toggle('secondary', waiting === 0);
  paintNote();
}

function setWaiting(count: number): void {
  waiting = count;
  paintBadge();
}

async function hostTick(): Promise<TickResult> {
  const share = getStoredShare();
  // Left the play screen, or this game was never shared: nothing to watch.
  if (!share || share.gameId !== hostGameId || !document.getElementById(CHECK_BUTTON_ID)) return 'stop';

  const signal = await fetchShareSignal(share.ownerToken);
  if (!signal.ok) {
    if (signal.gone) return 'stop';
    checkFailing = true;
    paintNote();
    return 'error';
  }
  lastCheckedAt = new Date();
  checkFailing = false;
  paintNote();
  if (signal.data === lastSignal) return 'unchanged';

  if (signal.data === null) {
    lastSignal = null;
    setWaiting(0);
    return 'changed';
  }
  const epoch = countEpoch;
  const count = needsExactRecount(Date.now(), lastCheckAt)
    ? await fetchPending(share.ownerToken).then((r) => (r.ok ? ({ ok: true, data: r.data.length } as const) : ({ ok: false, error: r.error, gone: false } as const)))
    : await fetchPendingCount(share.ownerToken);
  if (!count.ok) return count.gone ? 'stop' : 'error'; // lastSignal untouched, so the next tick retries the count
  // The host checked while this count was being fetched: its answer is newer and exact, so this one is dropped (lastSignal untouched,
  // so the next tick counts again if a guest really did submit something since).
  if (epoch !== countEpoch) return 'unchanged';
  lastSignal = signal.data;
  setWaiting(count.data);
  return 'changed';
}

/**
 * Call each time the host's play screen is shown. Does nothing for a game that
 * hasn't been shared, and does not restart a watcher that is already running for
 * this game (the screen re-renders often; the running watcher finds the new button).
 */
export function watchGuestRounds(gameId: string): void {
  if (getStoredShare()?.gameId !== gameId) {
    hostPoller?.stop();
    hostPoller = undefined;
    lastCheckedAt = undefined;
    checkFailing = false;
    setWaiting(0);
    return;
  }
  paintBadge();
  if (hostPoller?.isRunning() && hostGameId === gameId) return;
  hostPoller?.stop();
  if (hostGameId !== gameId) {
    waiting = 0;
    lastSignal = undefined;
    lastCheckedAt = undefined;
    checkFailing = false;
    lastCheckAt = 0;
    countEpoch++;
  }
  hostGameId = gameId;
  hostPoller = startPoller({ baseMs: HOST_POLL_MS, tick: hostTick });
  hostPoller.pokeNow(); // show anything already waiting without a 20 s delay
}

/**
 * After the host has applied guest rounds: show how many are still waiting (the ones that
 * failed validation stay pending). No network call — a round that arrives later changes the
 * stamp, and the next check recounts.
 */
export function setGuestRoundsWaiting(count: number): void {
  countEpoch++;
  lastCheckAt = Date.now();
  setWaiting(Math.max(0, count));
}

// ---------------------------------------------------------------------------
// Guest: live scoreboard
// ---------------------------------------------------------------------------

export interface GuestWatchHooks {
  /** False once the guest has moved to a different screen — the watcher then stops without making a call. */
  isShowing(): boolean;
  /** A newer game state arrived. */
  apply(next: SharedGameState): void;
  /** The link is no longer valid (expired, replaced, or the game was invalidated). */
  linkGone(): void;
  /** Called after every successful check with the time, so the screen can show it is live. */
  checked(at: Date): void;
  /** A check could not reach the server (not a bad link) — the screen should say so. */
  problem?(): void;
}

let guestPoller: Poller | undefined;

export function stopWatchingSharedGame(): void {
  guestPoller?.stop();
  guestPoller = undefined;
}

/**
 * Start (or restart) watching a shared game from a guest device. `initial` is the
 * state already on screen; the poller only asks for anything newer than it.
 */
export function watchSharedGame(token: string, initial: SharedGameState, hooks: GuestWatchHooks): void {
  stopWatchingSharedGame();
  // A finished game has nothing more to show (and the link expires an hour after
  // it ends); the Refresh button covers the rare "host went back to the game".
  if (initial.ended) return;

  let current = initial;
  guestPoller = startPoller({
    baseMs: GUEST_POLL_MS,
    tick: async () => {
      if (!hooks.isShowing()) return 'stop';
      const result = await fetchShareStateIfChanged(token, current.lastUpdatedAt);
      if (!result.ok) {
        if (result.gone) {
          hooks.linkGone();
          return 'stop';
        }
        hooks.problem?.();
        return 'error';
      }
      hooks.checked(new Date());
      if (result.data.unchanged) return 'unchanged';
      current = result.data.state;
      hooks.apply(current);
      return current.ended ? 'stop' : 'changed';
    },
  });
}
